import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import { useCreateResource, useDeleteResource, useUpdateResource } from '@app/src/api/updateHooks'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import type { Assertion } from '@app/src/types/reporting'
import type { Response } from '@app/src/types/resourceExplorer'
import { ResponseDraftStatus } from '@app/src/types/resourceExplorer'
import { AmplitudeTrackingEvents, NotificationSeverity, PrepareCardAction, PrepareCardId } from '@app/src/wf-constants'
import { consumePrepareCardSource } from '@app/src/utils/prepareCardTracking'
import { useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'
import type { FormData } from '../Provider/ProviderReport'
import { useReport } from '../ReportContext'
import { buildResponseItems } from './responseItems'

type UseProviderQuestionnaire = {
  createDraft: () => Promise<void>
  updateDraft: (values: FormData, autoSave?: boolean) => Promise<void>
  deleteDraft: () => Promise<void>
  reuseResponse: (responseId?: number) => Promise<void>
  submitResponse: (values: FormData, assertions: Assertion[], onSuccessCallback: () => void) => Promise<void>
  isLoadingCreateDraft: boolean
  isLoadingDeleteDraft: boolean
  isLoadingReuseResponse: boolean
  isLoadingUpdateSaveDraft: boolean
  isLoadingSubmitDraft: boolean
}

const useProviderQuestionnaire = (): UseProviderQuestionnaire => {
  const { request, response, reportStatus, updateResponseIdParam, locale } = useReport()
  const { formatMessage } = useIntl()
  const { showSnackbar } = useSnackbar()
  const { showErrorNotification } = useErrorNotification()
  const queryClient = useQueryClient()
  const { trackEvent } = useAmplitude()

  const trackResponseAction = (name: string) => {
    trackEvent({
      name,
      eventProps: {
        response_id: response?.id,
        request_id: request?.id,
      },
    })
  }

  const { mutateAsync: mutateCreateDraft, isPending: isLoadingCreateDraft } = useCreateResource<Response, {}>()
  const { mutateAsync: mutateUpdateDraft, isPending: isLoadingUpdateSaveDraft } = useUpdateResource<Response, {}>()
  const { mutateAsync: mutateSubmitDraft, isPending: isLoadingSubmitDraft } = useCreateResource<Response, {}>()
  const { mutateAsync: mutateDeleteDraft, isPending: isLoadingDeleteDraft } = useDeleteResource<Response, {}>()
  const { mutateAsync: mutateReuseResponse, isPending: isLoadingReuseResponse } = useUpdateResource<Response, {}>()

  const createDraft: UseProviderQuestionnaire['createDraft'] = async () => {
    if (reportStatus === ResponseDraftStatus.Draft || isLoadingCreateDraft) {
      return
    }

    await mutateCreateDraft(
      { url: endpoints.createResponseDraft, body: { requestId: request?.id } },
      {
        onSuccess: data => {
          updateResponseIdParam(data.id)
          queryClient.invalidateQueries({ queryKey: [FetchKey.RequestHistory] })
          queryClient.invalidateQueries({ queryKey: [FetchKey.Response, Number(response?.id), locale?.code] })
          trackResponseAction(AmplitudeTrackingEvents.Provider.Questionnaire.ResponseStarted)
        },
        onError: error => showErrorNotification({ requestError: error }),
      },
    )
  }

  const updateDraft: UseProviderQuestionnaire['updateDraft'] = async (values, autoSave) => {
    const responseItems = buildResponseItems(values)

    const body = {
      id: response?.id,
      requestId: request?.id,
      items: responseItems,
    }

    await mutateUpdateDraft(
      { url: endpoints.updateResponseDraft, body },
      {
        onSuccess: () => {
          showSnackbar({
            message: formatMessage({
              id: autoSave ? 'notifications.successfulResponseAutoSave' : 'notifications.successfulDraftSave',
            }),
            severity: 'success',
          })
          queryClient.invalidateQueries({ queryKey: [FetchKey.RequestHistory] })
          queryClient.invalidateQueries({ queryKey: [FetchKey.Response, Number(response?.id), locale?.code] })
        },
        onError: error => showErrorNotification({ requestError: error }),
      },
    )
  }

  const submitResponse: UseProviderQuestionnaire['submitResponse'] = async (values, assertions, onSuccessCallback) => {
    const responseItems = buildResponseItems(values)

    const body = {
      id: response?.id,
      requestId: request?.id,
      items: responseItems,
      responseStatus: ResponseDraftStatus.Submitted,
      assertions,
    }

    await mutateSubmitDraft(
      { url: endpoints.submitResponseDraft, body },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: [FetchKey.OpenRequests] }),
            queryClient.invalidateQueries({ queryKey: [FetchKey.Response, Number(response?.id)] }),
            queryClient.invalidateQueries({ queryKey: [FetchKey.RequestHistory, Number(request?.id)] }),
            queryClient.invalidateQueries({ queryKey: [FetchKey.ResponseSharing] }),
            queryClient.invalidateQueries({ queryKey: [FetchKey.RequestPerPeriod] }),
            queryClient.invalidateQueries({ queryKey: [FetchKey.AccessorOrganization] }),
            queryClient.invalidateQueries({ queryKey: [FetchKey.InquiryOpenRequests] }),
            queryClient.invalidateQueries({ queryKey: [FetchKey.Request] }),
          ])

          onSuccessCallback()

          trackResponseAction(AmplitudeTrackingEvents.Provider.Questionnaire.ResponseSubmitted)
          if (consumePrepareCardSource(PrepareCardId.Assess)) {
            trackEvent({
              name: AmplitudeTrackingEvents.Dashboard.PrepareForRequest.CardActionCompleted,
              eventProps: { card_id: PrepareCardId.Assess, action: PrepareCardAction.ResponseSubmitted },
            })
          }
        },
        onError: error => {
          if (error.isValidationError) {
            showSnackbar({ message: formatMessage({ id: 'notifications.submitValidationError' }), severity: 'warning' })
            return
          }
          showErrorNotification({ requestError: error })
        },
      },
    )
  }

  const deleteDraft: UseProviderQuestionnaire['deleteDraft'] = async () => {
    await mutateDeleteDraft(
      {
        url: endpoints.discardResponse(response?.id),
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: [FetchKey.RequestHistory, Number(request?.id)] })
          await queryClient.invalidateQueries({ queryKey: [FetchKey.RequestPerPeriod] })

          updateResponseIdParam(undefined)

          showSnackbar({
            message: formatMessage({ id: 'notifications.successfulResponseDiscard' }),
            severity: NotificationSeverity.success,
          })

          trackResponseAction(AmplitudeTrackingEvents.Provider.Questionnaire.ResponseDiscarded)
        },
        onError: error => showErrorNotification({ requestError: error }),
      },
    )
  }

  const reuseResponse: UseProviderQuestionnaire['reuseResponse'] = async (responseId = response?.id) => {
    await mutateReuseResponse(
      { url: endpoints.reuseResponse(request?.id, responseId), body: undefined },
      {
        onSuccess: data => {
          updateResponseIdParam(data.id)
          queryClient.invalidateQueries({ queryKey: [FetchKey.RequestHistory, Number(request?.id)] })
        },
        onError: error => showErrorNotification({ requestError: error }),
      },
    )
  }

  return {
    createDraft,
    updateDraft,
    deleteDraft,
    reuseResponse,
    submitResponse,
    isLoadingCreateDraft,
    isLoadingDeleteDraft,
    isLoadingReuseResponse,
    isLoadingUpdateSaveDraft,
    isLoadingSubmitDraft,
  }
}

export default useProviderQuestionnaire
